/**
 * R2 API Client — GenesisBox
 */

import { API_PATHS } from '@config/constants';
import { getApiBase, getAuthHeaders, readJsonResponse, type StorageScope } from './apiConfig';
import type { EmbedShare } from '@/types/embed.types';
import type { ListFilesResponse } from '@/types/file.types';
import type { UploadResponse, PresignedUrlResponse, PdfOpenResponse, HealthResponse, MoveResponse } from '@/types/api.types';
import type { AdminMember, ConnectionGrant, SavedConnection } from '@/types/connection.types';

const json = readJsonResponse;
const getManagementAuthHeaders = () => getAuthHeaders({ includeActiveConnection: false });

export async function checkHealth(): Promise<HealthResponse> {
  const res = await fetch(`${getApiBase()}${API_PATHS.health}`);
  return json(res);
}

export async function getCurrentSession() {
  const res = await fetch(`${getApiBase()}/auth/me`, { headers: await getAuthHeaders({ includeActiveConnection: false }) });
  return json(res);
}

export async function listConnections(): Promise<SavedConnection[]> {
  const res = await fetch(`${getApiBase()}/api/connections`, { headers: await getAuthHeaders({ includeActiveConnection: false }) });
  const body = await json(res);
  return body.connections || [];
}

export async function configureConnectionCors(storageScope?: StorageScope): Promise<{
  success: boolean;
  preservedRuleCount: number;
  rule: {
    AllowedOrigins: string[];
    AllowedMethods: string[];
  };
}> {
  const res = await fetch(`${getApiBase()}/api/connections/cors`, {
    method: 'POST',
    headers: {
      ...(await getAuthHeaders({ storageScope })),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });
  return json(res);
}

export async function listAdminMembers(): Promise<AdminMember[]> {
  const res = await fetch(`${getApiBase()}/api/admin/members`, { headers: await getManagementAuthHeaders() });
  const body = await json(res);
  return body.members || [];
}

export async function updateMemberApproval(email: string, isApproved: boolean) {
  const res = await fetch(`${getApiBase()}/api/admin/members/${encodeURIComponent(email)}`, {
    method: 'PATCH',
    headers: { ...(await getManagementAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ status: isApproved ? 'approved' : 'pending' }),
  });
  return json(res);
}

export async function saveMemberAssignment(email: string, connectionGrants: ConnectionGrant[]) {
  const res = await fetch(`${getApiBase()}/api/admin/assignments/${encodeURIComponent(email)}`, {
    method: 'PUT',
    headers: { ...(await getManagementAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ connectionGrants }),
  });
  return json(res);
}

export async function createAdminConnection(connection: SavedConnection): Promise<SavedConnection> {
  const res = await fetch(`${getApiBase()}/api/admin/connections`, {
    method: 'POST',
    headers: { ...(await getManagementAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify(connection),
  });
  const body = await json(res);
  return body.connection;
}

export async function updateAdminConnection(id: string, connection: SavedConnection): Promise<SavedConnection> {
  const res = await fetch(`${getApiBase()}/api/admin/connections/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { ...(await getManagementAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify(connection),
  });
  const body = await json(res);
  return body.connection;
}

export async function deleteAdminConnection(id: string): Promise<void> {
  const res = await fetch(`${getApiBase()}/api/admin/connections/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: await getManagementAuthHeaders(),
  });
  await json(res);
}

export async function listFiles(path = '', recursive = false, storageScope?: StorageScope): Promise<ListFilesResponse> {
  const params = new URLSearchParams();
  if (path) params.set('path', path);
  if (recursive) params.set('recursive', 'true');
  const url = `${getApiBase()}${API_PATHS.files}?${params}`;
  const res = await fetch(url, { headers: await getAuthHeaders({ storageScope }) });
  return json(res);
}

export async function downloadFile(path: string, storageScope?: StorageScope): Promise<Blob> {
  const params = new URLSearchParams({ path });
  const res = await fetch(`${getApiBase()}${API_PATHS.download}?${params}`, {
    headers: await getAuthHeaders({ storageScope }),
  });
  if (!res.ok) await json(res);
  return res.blob();
}

export async function getPresignedUrl(
  path: string,
  expiresIn = 3600,
  storageScope?: StorageScope,
): Promise<PresignedUrlResponse> {
  const params = new URLSearchParams({ path, expiresIn: String(expiresIn) });
  const res = await fetch(`${getApiBase()}${API_PATHS.url}?${params}`, {
    headers: await getAuthHeaders({ storageScope }),
  });
  return json(res);
}

export async function openPdfInGenesisView(
  path: string,
  storageScope?: StorageScope,
): Promise<PdfOpenResponse> {
  const res = await fetch(`${getApiBase()}/api/files/open-pdf`, {
    method: 'POST',
    headers: { ...(await getAuthHeaders({ storageScope })), 'Content-Type': 'application/json' },
    body: JSON.stringify({ path }),
  });
  return json(res);
}

export async function uploadFile(
  path: string,
  file: File | Blob,
  onProgress?: (pct: number) => void,
  storageScope?: StorageScope,
): Promise<UploadResponse> {
  const contentType = file.type || 'application/octet-stream';
  const presign = await fetch(`${getApiBase()}${API_PATHS.uploadUrl}`, {
    method: 'POST',
    headers: {
      ...(await getAuthHeaders({ storageScope })),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ path, contentType, size: file.size }),
  });

  if (presign.ok) {
    const ticket = await presign.json() as {
      url: string;
      headers?: Record<string, string>;
      operationId?: string;
      proxyFallbackMaxBytes?: number;
    };
    if (!ticket.operationId) throw new Error('Upload ticket has no completion operation');

    let direct: Response | null = null;
    let directError: unknown = null;
    try {
      direct = await fetch(ticket.url, {
        method: 'PUT',
        headers: ticket.headers || { 'Content-Type': contentType },
        body: file,
      });
      if (!direct.ok) directError = new Error(`Direct upload failed (HTTP ${direct.status})`);
    } catch (error) {
      directError = error;
    }

    if (!direct || !direct.ok) {
      const fallbackLimit = Number(ticket.proxyFallbackMaxBytes || 0);
      if (fallbackLimit <= 0 || file.size > fallbackLimit) {
        const reason = directError instanceof Error ? directError.message : 'Direct upload failed';
        throw new Error(`${reason}. Configure PUT CORS on the storage bucket and retry.`);
      }
      const params = new URLSearchParams({ path, operationId: ticket.operationId });
      const fallback = await fetch(`${getApiBase()}${API_PATHS.upload}?${params}`, {
        method: 'POST',
        headers: {
          ...(await getAuthHeaders({ storageScope })),
          'Content-Type': contentType,
        },
        body: file,
      });
      const uploaded = await json(fallback) as UploadResponse;
      if (onProgress) onProgress(100);
      return uploaded;
    }

    let completion: Response | null = null;
    for (let attempt = 0; attempt < 4; attempt += 1) {
      completion = await fetch(`${getApiBase()}/api/files/upload-complete`, {
        method: 'POST',
        headers: {
          ...(await getAuthHeaders({ storageScope })),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ operationId: ticket.operationId }),
      });
      if (completion.ok || (completion.status !== 409 && completion.status < 500) || attempt === 3) break;
      await new Promise((resolve) => setTimeout(resolve, 250 * (2 ** attempt)));
    }
    const completed = await json(completion as Response) as UploadResponse;
    if (onProgress) onProgress(100);
    return completed;
  }

  const presignError = await presign.json().catch(() => null);
  if (presign.status !== 409 || presignError?.code !== 'SFTP_PROXY_REQUIRED') {
    throw new Error(presignError?.error || `HTTP ${presign.status}`);
  }

  // SFTP has no browser-native presigned operation. Its proxy path streams
  // with backpressure and is capped by the server at 100 MB.
  const res = await fetch(`${getApiBase()}${API_PATHS.upload}?path=${encodeURIComponent(path)}`, {
    method: 'POST',
    headers: {
      ...(await getAuthHeaders({ storageScope })),
      'Content-Type': contentType,
    },
    body: file,
  });
  if (onProgress) onProgress(100);
  return json(res);
}

export async function deleteFile(path: string, storageScope?: StorageScope): Promise<{ success: boolean; path: string }> {
  const params = new URLSearchParams({ path });
  const res = await fetch(`${getApiBase()}${API_PATHS.files}?${params}`, {
    method: 'DELETE',
    headers: await getAuthHeaders({ storageScope }),
  });
  return json(res);
}

export async function createFolder(path: string, storageScope?: StorageScope): Promise<{ success: boolean; path: string; share?: EmbedShare | null }> {
  const res = await fetch(`${getApiBase()}${API_PATHS.folder}`, {
    method: 'POST',
    headers: { ...(await getAuthHeaders({ storageScope })), 'Content-Type': 'application/json' },
    body: JSON.stringify({ path }),
  });
  return json(res);
}

export async function moveFile(from: string, to: string, storageScope?: StorageScope): Promise<MoveResponse> {
  const res = await fetch(`${getApiBase()}${API_PATHS.move}`, {
    method: 'PUT',
    headers: { ...(await getAuthHeaders({ storageScope })), 'Content-Type': 'application/json' },
    body: JSON.stringify({ from, to }),
  });
  return json(res);
}

export interface BulkOpResult<T> {
  results: Array<T & { status: string; error?: string }>;
}

/** Bulk-delete (server cap 100). Returns per-path status. */
export async function bulkDeleteFiles(paths: string[], storageScope?: StorageScope): Promise<{
  deleted: number;
  errors: number;
  results: Array<{ path: string; status: string; error?: string }>;
}> {
  const res = await fetch(`${getApiBase()}/api/files/bulk-delete`, {
    method: 'POST',
    headers: { ...(await getAuthHeaders({ storageScope })), 'Content-Type': 'application/json' },
    body: JSON.stringify({ paths }),
  });
  return json(res);
}

/** Bulk-move (server cap 100). Returns per-move status. */
export async function bulkMoveFiles(moves: Array<{ from: string; to: string }>, storageScope?: StorageScope): Promise<{
  moved: number;
  errors: number;
  results: Array<{ from: string; to: string; status: string; error?: string }>;
}> {
  const res = await fetch(`${getApiBase()}/api/files/bulk-move`, {
    method: 'POST',
    headers: { ...(await getAuthHeaders({ storageScope })), 'Content-Type': 'application/json' },
    body: JSON.stringify({ moves }),
  });
  return json(res);
}

// ── Org Admin API ────────────────────────────────────────────────

export interface OrgSummary {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  memberCount?: number;
  connectionCount?: number;
}

export interface OrgMember {
  email: string;
  name?: string;
  picture?: string | null;
  provider?: string;
  orgId?: string;
  orgName?: string;
  role: string;
  status: string;
  connectionGrants: ConnectionGrant[];
  tokenVersion?: number;
  joinedAt?: string;
  lastLoginAt?: string;
}

export async function listAdminOrgs(): Promise<OrgSummary[]> {
  const res = await fetch(`${getApiBase()}/api/admin/orgs`, { headers: await getManagementAuthHeaders() });
  const body = await json(res);
  return body.orgs || [];
}

export async function createAdminOrg(name: string): Promise<OrgSummary> {
  const res = await fetch(`${getApiBase()}/api/admin/orgs`, {
    method: 'POST',
    headers: { ...(await getManagementAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  const body = await json(res);
  return body.org;
}

export async function deleteAdminOrg(orgId: string): Promise<void> {
  const res = await fetch(`${getApiBase()}/api/admin/orgs/${encodeURIComponent(orgId)}`, {
    method: 'DELETE',
    headers: await getManagementAuthHeaders(),
  });
  await json(res);
}

export async function listOrgMembers(orgId: string): Promise<OrgMember[]> {
  const res = await fetch(`${getApiBase()}/api/admin/orgs/${encodeURIComponent(orgId)}/members`, {
    headers: await getManagementAuthHeaders(),
  });
  const body = await json(res);
  return body.members || [];
}

export async function inviteOrgMember(orgId: string, email: string): Promise<OrgMember> {
  const res = await fetch(`${getApiBase()}/api/admin/orgs/${encodeURIComponent(orgId)}/members`, {
    method: 'POST',
    headers: { ...(await getManagementAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, status: 'pending', role: 'member', connectionGrants: [] }),
  });
  const body = await json(res);
  return body.member;
}

export async function listOrgConnections(orgId: string): Promise<SavedConnection[]> {
  const res = await fetch(`${getApiBase()}/api/admin/orgs/${encodeURIComponent(orgId)}/connections`, {
    headers: await getManagementAuthHeaders(),
  });
  const body = await json(res);
  return body.connections || [];
}

export async function createOrgConnection(orgId: string, connection: SavedConnection): Promise<SavedConnection> {
  const res = await fetch(`${getApiBase()}/api/admin/orgs/${encodeURIComponent(orgId)}/connections`, {
    method: 'POST',
    headers: { ...(await getManagementAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify(connection),
  });
  const body = await json(res);
  return body.connection;
}

export async function updateOrgConnection(orgId: string, id: string, connection: SavedConnection): Promise<SavedConnection> {
  const res = await fetch(`${getApiBase()}/api/admin/orgs/${encodeURIComponent(orgId)}/connections/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { ...(await getManagementAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify(connection),
  });
  const body = await json(res);
  return body.connection;
}

export async function deleteOrgConnection(orgId: string, id: string): Promise<void> {
  const res = await fetch(`${getApiBase()}/api/admin/orgs/${encodeURIComponent(orgId)}/connections/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: await getManagementAuthHeaders(),
  });
  await json(res);
}

export async function setOrgMemberGrants(orgId: string, email: string, connectionGrants: ConnectionGrant[]): Promise<OrgMember> {
  const res = await fetch(`${getApiBase()}/api/admin/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(email)}`, {
    method: 'PUT',
    headers: { ...(await getManagementAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ connectionGrants }),
  });
  const body = await json(res);
  return body.member;
}

export async function setOrgMemberStatus(orgId: string, email: string, status: string): Promise<OrgMember> {
  const res = await fetch(`${getApiBase()}/api/admin/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(email)}/status`, {
    method: 'PATCH',
    headers: { ...(await getManagementAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  const body = await json(res);
  return body.member;
}

export async function setOrgMemberRole(orgId: string, email: string, role: string): Promise<OrgMember> {
  const res = await fetch(`${getApiBase()}/api/admin/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(email)}/role`, {
    method: 'PATCH',
    headers: { ...(await getManagementAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
  const body = await json(res);
  return body.member;
}

export async function assignMemberToOrg(email: string, orgId: string): Promise<OrgMember> {
  const res = await fetch(`${getApiBase()}/api/admin/members/${encodeURIComponent(email)}/organization`, {
    method: 'PATCH',
    headers: { ...(await getManagementAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ orgId }),
  });
  const body = await json(res);
  return body.member;
}
